/*
 * Fig & Bloom - events page client bar.
 *
 * Progressive enhancement, deliberately. The markup ships as a working page without this
 * file: the client bar is a list of anchor links and every gallery is present and visible,
 * so a crawler and a no-JS visitor both get the whole page. This script layers three things
 * on top - filtering, pinning the bar, and scrolling to the chosen gallery.
 *
 * That ordering matters for this site. The galleries carry 215 images and the story copy;
 * building them client-side would have hidden all of it behind JS execution for the sake of
 * an interaction. Nothing here removes content from the DOM.
 *
 * PINNING. The bar is fixed, not position:sticky, because sticky is bounded by the bar's own
 * section, which is only as tall as the intro plus the bar itself. A sentinel above the bar
 * decides when to pin, a spacer holds its height so pinning does not jump the page, and the
 * theme's sticky header is measured rather than assumed - it is position:fixed at a height
 * that varies by breakpoint, and the bar has to clear it.
 *
 * RE-ENTRANT. Everything hangs off a controller that can be rebuilt, because the theme editor
 * re-renders a section on every settings change and there are 15 client blocks to author in
 * this one. Window listeners are bound once and delegate to whichever controller is current,
 * so repeated section loads neither pile up listeners nor leave a dead bar behind.
 */
(function () {
  // The script tag lives inside the client-bar section, so the theme editor can re-execute
  // this file when the section re-renders. Hand off to the instance already running rather
  // than starting a second one and doubling every window listener.
  if (window.__fbEventsShowcase) {
    window.__fbEventsShowcase.refresh();
    return;
  }

  // Breathing room between the pinned chrome and the top of a gallery we scroll to.
  var SCROLL_GAP = 16;

  // The tally under the client row reads better spelled out at the sizes an archive like this
  // actually reaches. Past twenty it goes back to digits rather than growing a number speller.
  var NUMBER_WORDS = [
    'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen',
    'Nineteen', 'Twenty'
  ];

  function spell(n) {
    return NUMBER_WORDS[n] || String(n);
  }

  function text(el) {
    return el ? (el.textContent || '').trim() : '';
  }

  // The chapter heading names the client and the event together ("MIMCO Mother's Day
  // Activation"), and the client is already authored on the tile - so the event type is the
  // heading with that prefix taken off. Only used when the block leaves Event type blank;
  // headings that do not start with the client name (a private commission billed under a
  // different name) fall through to the full heading, which is never wrong, only longer.
  function deriveKind(heading, client) {
    if (!heading || !client) return heading;
    var lowerH = heading.toLowerCase();
    var lowerC = client.toLowerCase();
    if (lowerH.indexOf(lowerC) !== 0) return heading;
    var rest = heading.slice(client.length).replace(/^[\s:\u2013\u2014-]+/, '');
    return rest || heading;
  }

  var current = null;
  var globalsBound = false;

  function throttled(fn) {
    var waiting = false;
    return function () {
      if (waiting) return;
      waiting = true;
      window.requestAnimationFrame(function () {
        waiting = false;
        fn();
      });
    };
  }

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function scrollLeftTo(el, left) {
    if (el.scrollTo) {
      el.scrollTo({ left: left, behavior: reduceMotion() ? 'auto' : 'smooth' });
    } else {
      el.scrollLeft = left;
    }
  }

  /* ------------------------------------------------------------------ controller */

  function build(wall) {
    var panels = Array.prototype.slice.call(document.querySelectorAll('[data-fb-client-panel]'));
    if (panels.length === 0) return null;

    var tiles = Array.prototype.slice.call(wall.querySelectorAll('[data-fb-client-tile]'));
    if (tiles.length === 0) return null;

    var bar = wall.querySelector('[data-fb-wall-bar]');
    var sentinel = wall.querySelector('[data-fb-wall-sentinel]');
    var spacer = wall.querySelector('[data-fb-wall-spacer]');
    var rail = wall.querySelector('[data-fb-rail]');
    var railWrap = wall.querySelector('[data-fb-rail-wrap]');
    var prevBtn = wall.querySelector('[data-fb-rail-prev]');
    var nextBtn = wall.querySelector('[data-fb-rail-next]');

    var indexEl = wall.querySelector('[data-fb-index]');
    var backWrap = wall.querySelector('[data-fb-index-back]');

    var root = document.documentElement;
    var byId = {};
    panels.forEach(function (p) {
      byId[p.getAttribute('data-fb-client-panel')] = p;
    });

    var activeId = null;
    var pinned = false;

    /* -------------------------------------------------------------- offsets */

    // Height of the theme's sticky header, measured rather than hardcoded - it differs
    // between desktop and mobile.
    //
    // Deliberately NOT scoped to .sticky. The class is only added once the visitor has
    // scrolled past the header, so on the first click from the top of the page the header is
    // still in normal flow and a .sticky-scoped query returns 0 - which scrolled the gallery
    // heading up underneath the header it was about to become. The element only exists at all
    // when the theme's sticky-header setting is on, so a non-sticky header still gives 0.
    function headerOffset() {
      var header = document.querySelector('.header__sticky');
      return header ? Math.round(header.getBoundingClientRect().height) : 0;
    }

    function barHeight() {
      return bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    }

    // Total pinned chrome above the content: header plus the bar. Published as a custom
    // property so the galleries' scroll-margin-top can use it and in-page anchors land below
    // the chrome instead of underneath it.
    function publishOffsets() {
      var header = headerOffset();
      root.style.setProperty('--fb-events-header-offset', header + 'px');
      root.style.setProperty('--fb-events-nav-offset', header + barHeight() + 'px');
    }

    function scrollTargetFor(panel) {
      var top = panel.getBoundingClientRect().top + window.pageYOffset;
      // The bar is pinned by the time we land, so always reserve its height - including the
      // first click, made while the bar is still in normal flow.
      return Math.max(0, top - headerOffset() - barHeight() - SCROLL_GAP);
    }

    function scrollToPanel(panel, smooth) {
      window.scrollTo({
        top: scrollTargetFor(panel),
        behavior: smooth && !reduceMotion() ? 'smooth' : 'auto'
      });
    }

    /* ---------------------------------------------------------- pin / unpin */

    function setPinned(on) {
      if (!bar || on === pinned) return;
      pinned = on;
      // Measured before the class flips, so this is the bar's height in normal flow.
      var h = barHeight();
      bar.classList.toggle('is-pinned', on);
      if (spacer) spacer.style.height = on ? h + 'px' : '';
      publishOffsets();
    }

    function updatePin() {
      if (!bar || !sentinel) return;
      var header = headerOffset();
      var past = sentinel.getBoundingClientRect().top <= header;

      // Release the bar once the open gallery has scrolled by: past the work, a floating row
      // of logos over the enquiry form is noise rather than navigation.
      var panel = activeId && byId[activeId];
      var stillRelevant = true;
      if (panel && !panel.hidden) {
        stillRelevant = panel.getBoundingClientRect().bottom > header + barHeight();
      }

      setPinned(past && stillRelevant);
    }

    /* --------------------------------------------------------------- the rail */

    // Fades and nudge buttons only appear when the row actually overflows, which depends on
    // viewport width and how many clients are configured.
    function updateRailAffordances() {
      if (!rail || !railWrap) return;
      var overflow = rail.scrollWidth - rail.clientWidth;
      var hasOverflow = overflow > 2;
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft >= overflow - 2;

      railWrap.classList.toggle('has-nudge', hasOverflow);
      railWrap.classList.toggle('has-overflow-start', hasOverflow && !atStart);
      railWrap.classList.toggle('has-overflow-end', hasOverflow && !atEnd);

      // A button that cannot move the row is not shown at all - a permanently dead arrow at
      // the start edge reads as broken rather than as a limit.
      if (prevBtn) prevBtn.hidden = !hasOverflow || atStart;
      if (nextBtn) nextBtn.hidden = !hasOverflow || atEnd;
    }

    function nudge(direction) {
      if (!rail) return;
      var step = Math.max(160, Math.round(rail.clientWidth * 0.6));
      scrollLeftTo(rail, rail.scrollLeft + direction * step);
    }

    // Keep the active tile visible in the row without touching vertical scroll -
    // scrollIntoView would drag the page as well, which is exactly what we are fixing.
    function revealTile(tile) {
      if (!rail || !tile) return;
      var railBox = rail.getBoundingClientRect();
      var tileBox = tile.getBoundingClientRect();
      var pad = 62; // clears the edge fade and the nudge button
      var delta = 0;

      if (tileBox.left < railBox.left + pad) {
        delta = tileBox.left - railBox.left - pad;
      } else if (tileBox.right > railBox.right - pad) {
        delta = tileBox.right - railBox.right + pad;
      }
      if (delta !== 0) scrollLeftTo(rail, rail.scrollLeft + delta);
    }

    /* ----------------------------------------------------------------- index */

    // Read the archive off the page rather than out of a second set of blocks. Every fact a
    // card needs is already rendered: the chapter supplies the lead image, the heading and the
    // photograph count, and the tile supplies the client name and event type. Adding an event
    // therefore adds a card, with nothing to keep in step by hand.
    function collect() {
      return panels.map(function (panel) {
        var id = panel.getAttribute('data-fb-client-panel');
        var tile = wall.querySelector('[data-fb-client-tile="' + id.replace(/"/g, '\\"') + '"]');
        var images = panel.querySelectorAll('img');
        var heading = text(panel.querySelector('.fbed-ch__heading'));
        var client = (tile && tile.getAttribute('data-fb-client-name')) || heading;
        var kind = (tile && tile.getAttribute('data-fb-client-kind')) || deriveKind(heading, client);
        return {
          id: id,
          client: client,
          kind: kind === client ? '' : kind,
          count: images.length,
          lead: images[0] || null
        };
      });
    }

    function buildIndex() {
      if (!indexEl || !backWrap) return;

      // Emptied first: the theme editor rebuilds this controller whenever any section on the
      // page re-renders, and the wall's own element survives that, so appending without
      // clearing would stack a second archive under the first.
      indexEl.innerHTML = '';
      backWrap.innerHTML = '';

      var items = collect();
      var total = items.reduce(function (sum, it) {
        return sum + it.count;
      }, 0);

      // State the size of the archive under the client row, then prove it with the grid. The
      // counts are read off the page every load, so they cannot drift the way a typed-in
      // subheading would.
      //
      // The element is created when it is missing: the section only renders the subheading
      // paragraph when that setting is filled in, and it is deliberately left empty precisely
      // because these numbers should not be typed by hand. An authored subheading wins - it is
      // only ever overwritten when this script is the one that put it there.
      var sub = wall.querySelector('.fbwall__sub');
      if (!sub) {
        var intro = wall.querySelector('.fbwall__intro');
        if (intro) {
          sub = document.createElement('p');
          sub.className = 'fbwall__sub';
          sub.setAttribute('data-fb-auto-sub', '');
          intro.appendChild(sub);
        }
      }
      if (sub && (sub.hasAttribute('data-fb-auto-sub') || !text(sub))) {
        sub.textContent = spell(items.length) + ' events. ' + total + ' photographs.';
      }

      var head = document.createElement('div');
      head.className = 'fbidx__head';
      var h2 = document.createElement('h2');
      h2.textContent = 'Selected work';
      var tally = document.createElement('p');
      tally.className = 'fbidx__tally';
      tally.textContent = items.length + ' events · ' + total + ' photographs';
      head.appendChild(h2);
      head.appendChild(tally);

      var grid = document.createElement('div');
      grid.className = 'fbidx__grid';

      items.forEach(function (it) {
        // An anchor, not a button: without the enhancement this would still be a working jump
        // link, and it keeps the card reachable by keyboard and openable in a new tab.
        var card = document.createElement('a');
        card.className = 'fbidx__card';
        card.href = '#' + it.id;
        card.setAttribute('data-fb-index-card', it.id);

        var shot = document.createElement('span');
        shot.className = 'fbidx__shot';
        if (it.lead) {
          var img = document.createElement('img');
          img.src = it.lead.getAttribute('src') || '';
          var srcset = it.lead.getAttribute('srcset');
          if (srcset) {
            img.setAttribute('srcset', srcset);
            // Let the browser pick a thumbnail-sized file rather than the full-width one the
            // chapter would use - fifteen cards at gallery resolution would be a real cost.
            //
            // Tracks the three-column grid: the column tops out at 360px once the 1160px
            // measure is capped, and is roughly a third of the viewport below that. Understate
            // these and the browser picks a file too small for the frame and renders it soft,
            // which on a page selling photography is the more expensive mistake.
            img.setAttribute('sizes', '(min-width: 1204px) 360px, (min-width: 750px) 31vw, 46vw');
          }
          img.loading = 'lazy';
          img.decoding = 'async';
          img.alt = it.kind ? it.client + ', ' + it.kind : it.client;
          shot.appendChild(img);
        }
        card.appendChild(shot);

        var name = document.createElement('span');
        name.className = 'fbidx__name';
        name.textContent = it.client;
        card.appendChild(name);

        if (it.kind) {
          var kind = document.createElement('span');
          kind.className = 'fbidx__kind';
          kind.textContent = it.kind;
          card.appendChild(kind);
        }

        var count = document.createElement('span');
        count.className = 'fbidx__count';
        count.textContent = it.count + (it.count === 1 ? ' photograph' : ' photographs');
        card.appendChild(count);

        grid.appendChild(card);
      });

      indexEl.appendChild(head);
      indexEl.appendChild(grid);

      var back = document.createElement('button');
      back.className = 'fbidx__back';
      back.type = 'button';
      back.setAttribute('data-fb-index-backbtn', '');
      back.textContent = '← All work (' + items.length + ')';
      backWrap.appendChild(back);
    }

    // The landing state. Nothing is open, so the visitor is looking at the whole archive
    // rather than at one event that reads as the whole portfolio.
    function showIndex(opts) {
      var options = opts || {};
      activeId = null;

      panels.forEach(function (p) {
        p.hidden = true;
      });
      tiles.forEach(function (t) {
        t.removeAttribute('aria-current');
      });

      if (indexEl) indexEl.hidden = false;
      if (backWrap) backWrap.hidden = true;

      if (options.updateHash && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      updatePin();
    }

    /* ---------------------------------------------------------------- select */

    function select(id, opts) {
      var options = opts || {};
      if (!byId[id]) return false;

      activeId = id;

      panels.forEach(function (p) {
        var on = p.getAttribute('data-fb-client-panel') === id;
        p.hidden = !on;
      });

      // Swap the archive for the one case study, and put the way back out where the visitor
      // is already looking - directly above what they just opened.
      if (indexEl) indexEl.hidden = true;
      if (backWrap) backWrap.hidden = false;

      var activeTile = null;
      tiles.forEach(function (t) {
        var on = t.getAttribute('data-fb-client-tile') === id;
        // aria-current marks the active tab for assistive tech and drives the active style.
        if (on) {
          t.setAttribute('aria-current', 'true');
          activeTile = t;
        } else {
          t.removeAttribute('aria-current');
        }
      });

      if (activeTile) revealTile(activeTile);
      updateRailAffordances();

      if (options.updateHash && window.history && window.history.replaceState) {
        // replaceState, not a hash assignment: changing location.hash would jump the page.
        window.history.replaceState(null, '', '#' + id);
      }
      return true;
    }

    /* ---------------------------------------------------------------- wiring */

    tiles.forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        var id = tile.getAttribute('data-fb-client-tile');
        if (!byId[id]) return; // fall through to the anchor's default behaviour
        e.preventDefault();
        select(id, { updateHash: true });

        // Always take the visitor to the work. The old behaviour only scrolled when the
        // gallery was above the viewport, so clicking from the top of the page swapped the
        // content silently and left you looking at the logos.
        scrollToPanel(byId[id], true);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        nudge(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        nudge(1);
      });
    }
    if (rail) {
      rail.addEventListener('scroll', throttled(updateRailAffordances), { passive: true });
    }

    // Delegated, because the cards are built after this runs and are rebuilt whenever the
    // theme editor re-renders the section.
    if (indexEl) {
      indexEl.addEventListener('click', function (e) {
        var card = e.target.closest && e.target.closest('[data-fb-index-card]');
        if (!card) return;
        var id = card.getAttribute('data-fb-index-card');
        if (!byId[id]) return; // fall through to the anchor's default behaviour
        e.preventDefault();
        select(id, { updateHash: true });
        scrollToPanel(byId[id], true);
      });
    }

    if (backWrap) {
      backWrap.addEventListener('click', function (e) {
        if (!e.target.closest || !e.target.closest('[data-fb-index-backbtn]')) return;
        var from = activeId;
        showIndex({ updateHash: true });
        // Land on the card you just came out of rather than at the top of the grid, so
        // leaving a case study returns you to your place in the archive.
        var card = indexEl && indexEl.querySelector('[data-fb-index-card="' + String(from).replace(/"/g, '\\"') + '"]');
        if (card) {
          window.scrollTo({
            top: Math.max(0, card.getBoundingClientRect().top + window.pageYOffset - headerOffset() - barHeight() - SCROLL_GAP),
            behavior: reduceMotion() ? 'auto' : 'smooth'
          });
        }
      });
    }

    var controller = {
      wall: wall,
      byId: byId,
      select: select,
      showIndex: showIndex,
      updatePin: updatePin,
      publishOffsets: publishOffsets,
      updateRailAffordances: updateRailAffordances,
      scrollToPanel: scrollToPanel,
      settle: function () {
        publishOffsets();
        updateRailAffordances();
        updatePin();
      },
      remeasure: function () {
        // Unpin first so the bar is measured in normal flow, then re-evaluate at the new size.
        setPinned(false);
        this.settle();
      },
      teardown: function () {
        setPinned(false);
        root.style.removeProperty('--fb-events-header-offset');
        root.style.removeProperty('--fb-events-nav-offset');
      }
    };

    /* ----------------------------------------------------------------- start */

    // Only hide panels once we know the enhancement is running, so a JS failure before this
    // point leaves every gallery visible rather than leaving the page blank.
    root.classList.add('fb-events-enhanced');

    // Built before anything is hidden, while the panels are still laid out in normal flow.
    buildIndex();

    publishOffsets();
    updateRailAffordances();

    // Deep links keep working: /pages/events#samantha-s-kitchen-tea-party opens that client.
    // Everyone else lands on the index rather than on whichever case study happens to be
    // authored first - the page opened on one event and was read as having only that one.
    var fromHash = window.location.hash.replace('#', '');
    var deepLinked = fromHash && select(fromHash);
    if (!deepLinked) {
      showIndex();
    }
    updatePin();

    if (deepLinked) {
      // The browser has already jumped to the anchor by now, but it did so before the bar
      // existed as pinned chrome. Re-seat it under the header and bar, without animation
      // since this is the arrival position, not a move the visitor asked for.
      window.requestAnimationFrame(function () {
        scrollToPanel(byId[fromHash], false);
        updatePin();
      });
    }

    return controller;
  }

  /* -------------------------------------------------------------- global wiring */

  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;

    window.addEventListener(
      'scroll',
      throttled(function () {
        if (current) current.updatePin();
      }),
      { passive: true }
    );

    window.addEventListener(
      'resize',
      throttled(function () {
        if (current) current.remeasure();
      })
    );

    window.addEventListener('hashchange', function () {
      if (!current) return;
      var id = window.location.hash.replace('#', '');
      // Back out of a case study with the browser's own back button and the hash empties -
      // which should return the archive, not strand the visitor on the open event.
      if (!id) {
        current.showIndex();
        return;
      }
      if (current.select(id)) current.scrollToPanel(current.byId[id], true);
    });

    // Images settle late on a page this heavy, which changes where the galleries start.
    window.addEventListener(
      'load',
      throttled(function () {
        if (current) current.settle();
      })
    );

    // Theme editor. A settings change re-renders the section, so rebuild against the new DOM -
    // forced, because a reloaded gallery leaves the bar's element untouched while its panel
    // list goes stale. Selecting a gallery in the editor sidebar opens that client rather than
    // leaving the author looking at a hidden section.
    document.addEventListener('shopify:section:load', function () {
      init(true);
    });
    document.addEventListener('shopify:section:select', function (e) {
      if (!current || !e.target) return;
      // The editor targets the section wrapper, which contains the panel; accept the panel
      // itself too so this does not depend on how the theme wraps sections.
      var target = e.target;
      var panel = null;
      if (target.matches && target.matches('[data-fb-client-panel]')) {
        panel = target;
      } else if (target.querySelector) {
        panel = target.querySelector('[data-fb-client-panel]');
      }
      var id = panel && panel.getAttribute('data-fb-client-panel');
      if (id) current.select(id);
    });
  }

  function init(force) {
    var wall = document.querySelector('[data-fb-client-wall]');
    if (!wall) {
      if (current) current.teardown();
      current = null;
      return;
    }
    if (!force && current && current.wall === wall) return; // already live against this DOM

    if (current) current.teardown();
    current = build(wall);
    bindGlobals();
  }

  window.__fbEventsShowcase = {
    refresh: function () {
      init(true);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        init();
      },
      { once: true }
    );
  } else {
    init();
  }
})();
